import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Star, MapPin, Phone, Mail, Clock, Scissors } from "lucide-react";

const NAV = [
  { label: "Behandlinger", href: "#nf-behandlinger" },
  { label: "Om salonen", href: "#nf-om" },
  { label: "Galleri", href: "#nf-galleri" },
  { label: "Åbningstider", href: "#nf-tider" },
  { label: "Kontakt", href: "#nf-kontakt" },
];

const PRICES = [
  ["Herreklipning", "349 kr."],
  ["Klipning + skæg", "499 kr."],
  ["Skægtrim & form", "199 kr."],
  ["Varm barbering m. håndklæde", "299 kr."],
  ["Børneklipning (under 12)", "249 kr."],
  ["Hovedbundsmassage", "149 kr."],
];

const REVIEWS = [
  {
    name: "Mikkel H.",
    text: "Rolig stemning, skarpt håndværk og et resultat, der holder uge efter uge.",
  },
  {
    name: "Jonas P.",
    text: "Nem booking og altid tid til en snak. Min faste barber fra nu af.",
  },
  {
    name: "Emil S.",
    text: "Skægtrimmet sidder lige i skabet. Professionelt fra start til slut.",
  },
];

const HOURS = [
  ["Mandag – fredag", "9.00 – 18.00"],
  ["Lørdag", "9.00 – 15.00"],
  ["Søndag", "Lukket"],
];

const GALLERY = [
  "https://images.unsplash.com/photo-1503951914875-452162b0f3f1?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  "https://images.unsplash.com/photo-1599351431202-1e0f0137899a?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  "https://images.unsplash.com/photo-1605497788044-5a32c7078486?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  "https://images.unsplash.com/photo-1585747860715-2ba37e788b70?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
];

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] },
});

const Label = ({ children }) => (
  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C8A96A] mb-4">
    {children}
  </p>
);

export default function BarbershopCase({ onNavigate }) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const go = (href) => (e) => {
    e.preventDefault();
    onNavigate?.(href);
  };

  return (
    <div className="bg-zinc-950 text-zinc-100 antialiased min-h-screen" data-testid="barbershop-case-page">
      <div className="bg-white text-zinc-900 text-xs sm:text-sm py-2.5 px-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center">
        <span className="font-medium">Konceptprojekt – ikke en rigtig kunde. Designet af Naveli.</span>
        <Link to="/" data-testid="case-back-link" className="inline-flex items-center gap-1 font-semibold underline underline-offset-2">
          <ArrowLeft className="w-3.5 h-3.5" aria-hidden="true" />
          Tilbage til Naveli
        </Link>
      </div>

      <header className="sticky top-0 z-50 bg-zinc-950/85 backdrop-blur-md border-b border-zinc-800/80">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-[68px] flex items-center justify-between">
          <a href="#nf-top" onClick={go("#nf-top")} data-testid="case-logo" className="flex items-center gap-2.5 font-bold tracking-[0.15em] text-sm">
            <Scissors className="w-4 h-4 text-[#C8A96A]" aria-hidden="true" />
            NORDIC FADE
          </a>
          <nav className="hidden md:flex items-center gap-7" aria-label="Salonmenu">
            {NAV.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={go(l.href)}
                data-testid={`case-nav-${l.href.replace("#nf-", "")}`}
                className="text-sm text-zinc-400 hover:text-white transition-colors duration-200"
              >
                {l.label}
              </a>
            ))}
          </nav>
          <a
            href="#nf-kontakt"
            onClick={go("#nf-kontakt")}
            data-testid="case-book-cta"
            className="inline-flex items-center gap-2 bg-[#C8A96A] text-zinc-950 text-sm font-semibold rounded-full px-5 py-2.5 hover:bg-[#d8ba7e] hover:-translate-y-px transition-all duration-200"
          >
            Book tid
            <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </a>
        </div>
      </header>

      <section id="nf-top" className="relative min-h-[82vh] flex items-center overflow-hidden" data-testid="case-hero">
        <img
          src="https://images.unsplash.com/photo-1503951914875-452162b0f3f1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800"
          alt="Barber i arbejde hos Nordic Fade"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/80 via-zinc-950/60 to-zinc-950" aria-hidden="true" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-24">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-xs font-semibold uppercase tracking-[0.25em] text-[#C8A96A] mb-6"
          >
            Barbershop · København N
          </motion.p>
          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.05] max-w-3xl">
            <span className="block overflow-hidden pb-1">
              <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}>
                Skarpe klipninger.
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-1">
              <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}>
                Ægte håndværk.
              </motion.span>
            </span>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.55 }}
            className="mt-6 text-lg text-zinc-300 max-w-xl leading-relaxed"
          >
            Klassisk barbering og moderne herreklip i afslappede omgivelser. Book din
            tid online på under et minut.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="mt-9 flex flex-col sm:flex-row gap-4"
          >
            <a
              href="#nf-kontakt"
              onClick={go("#nf-kontakt")}
              data-testid="case-hero-book-button"
              className="inline-flex items-center justify-center gap-2 bg-[#C8A96A] text-zinc-950 font-semibold rounded-full px-8 py-4 hover:bg-[#d8ba7e] hover:-translate-y-0.5 transition-all duration-200"
            >
              Book tid
              <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </a>
            <a
              href="#nf-behandlinger"
              onClick={go("#nf-behandlinger")}
              data-testid="case-hero-prices-button"
              className="inline-flex items-center justify-center gap-2 border border-zinc-600 text-white font-medium rounded-full px-8 py-4 hover:border-white hover:-translate-y-0.5 transition-all duration-200"
            >
              Se priser
            </a>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-zinc-400"
          >
            <span className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#C8A96A]" aria-hidden="true" />
              Man–Fre 9–18 · Lør 9–15
            </span>
            <span className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#C8A96A]" aria-hidden="true" />
              Jægersborggade 12, København N
            </span>
          </motion.div>
        </div>
      </section>

      <section id="nf-behandlinger" className="py-20 sm:py-28" data-testid="case-services">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div {...fadeUp()}>
            <Label>Behandlinger & priser</Label>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Håndværk til ærlige priser</h2>
          </motion.div>
          <div className="mt-12 grid sm:grid-cols-2 gap-x-16">
            {PRICES.map(([name, price], i) => (
              <motion.div key={name} {...fadeUp(i * 0.06)} className="flex items-baseline justify-between gap-4 py-5 border-b border-zinc-800">
                <span className="text-base text-zinc-200">{name}</span>
                <span className="flex-1 border-b border-dotted border-zinc-700 -translate-y-1" aria-hidden="true" />
                <span className="text-base font-semibold text-[#C8A96A] whitespace-nowrap">{price}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="nf-om" className="py-20 sm:py-28 bg-zinc-900/50" data-testid="case-about">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid gap-12 lg:grid-cols-2 items-center">
          <motion.div {...fadeUp()}>
            <Label>Om salonen</Label>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">En barber, du kan stole på</h2>
            <p className="mt-6 text-base sm:text-lg text-zinc-400 leading-relaxed">
              Nordic Fade er en lokal barbershop med fokus på klassisk håndværk,
              rolig atmosfære og tid til den enkelte kunde. Ingen stress, ingen
              ventekø — bare en skarp klipning, hver gang.
            </p>
            <p className="mt-4 text-base sm:text-lg text-zinc-400 leading-relaxed">
              Vi bruger nøje udvalgte produkter og tager os tid til at forstå,
              præcis hvad du ønsker, før saksen går i gang.
            </p>
          </motion.div>
          <motion.div {...fadeUp(0.15)} className="rounded-2xl overflow-hidden aspect-[4/3] bg-zinc-800">
            <img
              src="https://images.unsplash.com/photo-1605497788044-5a32c7078486?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
              alt="Skægtrim hos Nordic Fade"
              loading="lazy"
              className="w-full h-full object-cover"
            />
          </motion.div>
        </div>
      </section>

      <section className="py-20 sm:py-28" data-testid="case-reviews">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div {...fadeUp()}>
            <Label>Kunderne siger</Label>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Ord fra stolen</h2>
            <p className="mt-3 text-sm text-zinc-500">Fiktive demoanmeldelser — vist som layouteksempel.</p>
          </motion.div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {REVIEWS.map((r, i) => (
              <motion.figure key={r.name} {...fadeUp(i * 0.1)} className="bg-zinc-900 border border-zinc-800 rounded-2xl p-7">
                <div className="flex gap-1 mb-4" aria-label="5 ud af 5 stjerner">
                  {[...Array(5)].map((_, s) => (
                    <Star key={s} className="w-4 h-4 fill-[#C8A96A] text-[#C8A96A]" aria-hidden="true" />
                  ))}
                </div>
                <blockquote className="text-base text-zinc-300 leading-relaxed">“{r.text}”</blockquote>
                <figcaption className="mt-5 text-sm font-medium text-zinc-500">{r.name}</figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      <section id="nf-galleri" className="py-20 sm:py-28 bg-zinc-900/50" data-testid="case-gallery">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div {...fadeUp()}>
            <Label>Galleri</Label>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Stemningen i salonen</h2>
          </motion.div>
          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {GALLERY.map((src, i) => (
              <motion.div key={src} {...fadeUp(i * 0.08)} className="group rounded-xl overflow-hidden aspect-square bg-zinc-800">
                <img
                  src={src}
                  alt={`Galleribillede ${i + 1} fra Nordic Fade`}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="nf-tider" className="py-20 sm:py-28" data-testid="case-hours">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid gap-12 lg:grid-cols-2">
          <motion.div {...fadeUp()}>
            <Label>Åbningstider</Label>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Vi er her, når det passer dig</h2>
            <div className="mt-10 border-t border-zinc-800">
              {HOURS.map(([day, time]) => (
                <div key={day} className="flex items-center justify-between py-5 border-b border-zinc-800">
                  <span className="text-base text-zinc-300">{day}</span>
                  <span className="text-base font-semibold text-white">{time}</span>
                </div>
              ))}
            </div>
          </motion.div>
          <motion.div {...fadeUp(0.15)}>
            <Label>Find vej</Label>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Jægersborggade 12, 2200 København N</h2>
            <div className="mt-10 rounded-2xl overflow-hidden border border-zinc-800 aspect-[4/3] bg-zinc-800">
              <iframe
                title="Kort over Nordic Fade, Jægersborggade 12, København N"
                src="https://www.google.com/maps?q=J%C3%A6gersborggade%2012%2C%202200%20K%C3%B8benhavn%20N&output=embed"
                className="w-full h-full border-0"
                loading="lazy"
                data-testid="case-map"
              />
            </div>
          </motion.div>
        </div>
      </section>

      <section id="nf-kontakt" className="py-20 sm:py-28 bg-zinc-900/50" data-testid="case-contact">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <motion.div {...fadeUp()}>
            <Label>Kontakt & booking</Label>
            <h2 className="text-3xl sm:text-5xl font-bold tracking-tight max-w-2xl mx-auto">
              Klar til en frisk klipning?
            </h2>
            <p className="mt-5 text-base sm:text-lg text-zinc-400 max-w-xl mx-auto">
              Ring, skriv eller kig forbi salonen. Vi finder en tid, der passer ind i
              din hverdag.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="tel:+4512345678"
                data-testid="case-phone-link"
                className="inline-flex items-center gap-2 bg-[#C8A96A] text-zinc-950 font-semibold rounded-full px-8 py-4 hover:bg-[#d8ba7e] hover:-translate-y-0.5 transition-all duration-200"
              >
                <Phone className="w-4 h-4" aria-hidden="true" />
                Ring 12 34 56 78
              </a>
              <a
                href="mailto:booking@nordicfade.dk"
                data-testid="case-email-link"
                className="inline-flex items-center gap-2 border border-zinc-600 text-white font-medium rounded-full px-8 py-4 hover:border-white hover:-translate-y-0.5 transition-all duration-200"
              >
                <Mail className="w-4 h-4" aria-hidden="true" />
                booking@nordicfade.dk
              </a>
            </div>
            <p className="mt-6 text-xs text-zinc-500">
              Demo — telefonnummer og e-mail tilhører ikke en rigtig virksomhed.
            </p>
          </motion.div>
        </div>
      </section>

      <footer className="border-t border-zinc-800 py-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-zinc-500">
          <span className="flex items-center gap-2 font-semibold tracking-[0.15em] text-zinc-300">
            <Scissors className="w-4 h-4 text-[#C8A96A]" aria-hidden="true" />
            NORDIC FADE
          </span>
          <p className="text-center">Konceptprojekt lavet af Naveli — ikke en rigtig kunde.</p>
          <Link to="/" data-testid="case-footer-back-link" className="inline-flex items-center gap-1 hover:text-white transition-colors duration-200">
            <ArrowLeft className="w-4 h-4" aria-hidden="true" />
            Tilbage til Naveli
          </Link>
        </div>
      </footer>
    </div>
  );
}
